/**
 * F 381 — THE LANE CHAT PAGE.
 *
 * A surface that talks to the Lane Bridge: you type a message, it becomes a
 * `turn` in the FCC graph, the VPS bridge types it into DeepSeek with a saved
 * session cookie, scrapes the reply, and writes it back into the same turn.
 *
 * WHAT THIS IS NOT
 * ----------------
 * This is not a second Agent Chat. The existing Agent Chat (`AIChatPanel`) is
 * a Groq-backed agent that runs a tool loop in the browser; this is a thin
 * window onto the graph, and the only actor on the other end is the bridge
 * script on the VPS. It renders turns and writes turns. It has no model, no
 * key, and no provider.
 *
 * THE POLL
 * --------
 * `RemoteState` hydrates once at boot and never polls, so a reply written by
 * the bridge would sit on the server unseen until a reload. This page polls
 * `/api/fcc/kv?prefix=freshcards:turn:` — the same route `RemoteState` itself
 * uses — and merges what it finds. The poll degrades to nothing on a static
 * deploy; the page then shows only what is in the local cache.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Group, Stack, Text, Textarea, Button, Loader, Badge, ScrollArea, Paper } from '@mantine/core';
import { IconSend, IconRobot, IconUser } from '@tabler/icons-react';
import {
  ensureLaneAgent,
  ensureLaneConversation,
  ensureLaneProject,
  isPendingTurn,
  listLaneTurns,
  makeUserTurn,
  saveLaneNode,
  LANE_AGENT_LABEL,
} from '../../../core/lane/laneGraph';
import type { Turn } from '../../../types/models/Turn';
import './LaneChat.css';

const POLL_MS = 2000;

export interface LaneChatProps {
  id?: string;
  title?: string;
}

export const LaneChat: React.FC<LaneChatProps> = ({ id = 'lane-chat', title = 'Lane Chat' }) => {
  const [turns, setTurns] = useState<Turn[]>([]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [agentId, setAgentId] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  const refresh = useCallback(async () => {
    try {
      const id_ = agentId;
      if (!id_) return;
      setTurns(await listLaneTurns(id_));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [agentId]);

  // Seed the lane's project, agent and conversation, then start polling.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const project = await ensureLaneProject();
        const agent = await ensureLaneAgent(project);
        const conversation = await ensureLaneConversation(project, agent.id);
        if (cancelled) return;
        setAgentId(agent.id);
        setTurns(await listLaneTurns(agent.id));
        setReady(true);
        void conversation;
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
        setReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // The poll. This is the difference between "the reply arrives" and "the
  // reply arrives after a manual reload".
  useEffect(() => {
    if (!ready) return;
    const timer = setInterval(() => {
      void refresh();
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [ready, refresh]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [turns.length]);

  const send = useCallback(async () => {
    const message = text.trim();
    if (!message || busy || !agentId) return;
    setBusy(true);
    setError(null);
    try {
      const project = await ensureLaneProject();
      const conversation = await ensureLaneConversation(project, agentId);
      const turn = makeUserTurn({
        text: message,
        project: project.id,
        conversationId: conversation.id,
        agentId,
        sequence: turns.length + 1,
      });
      await saveLaneNode('turn', turn.id, turn);
      setText('');
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }, [text, busy, agentId, turns.length, refresh]);

  const pending = useMemo(() => turns.filter(isPendingTurn).length, [turns]);

  return (
    <Box className="lane-chat" data-testid="lane-chat" id={id}>
      <Group justify="space-between" mb="sm">
        <Text fw={700} size="lg">Lane Chat</Text>
        <Group gap="xs">
          <Badge size="sm" variant="light" color="grape" data-testid="lane-chat-agent">
            {LANE_AGENT_LABEL} agent
          </Badge>
          {pending > 0 && (
            <Badge size="sm" variant="light" color="yellow" data-testid="lane-chat-pending">
              {pending} awaiting reply
            </Badge>
          )}
        </Group>
      </Group>

      <Text size="sm" c="dimmed" mb="md">
        Messages become <code>turn</code> nodes in the FCC graph. The Lane Bridge on the VPS types
        them into DeepSeek with a saved session cookie and writes the reply back into the same
        turn.
      </Text>

      <ScrollArea h="calc(100vh - 320px)" type="auto" offsetScrollbars>
        <Stack gap="sm" pr="sm">
          {turns.length === 0 && ready && (
            <Text size="sm" c="dimmed" data-testid="lane-chat-empty">
              No messages yet. Say something.
            </Text>
          )}
          {turns.map((turn) => (
            <Paper key={turn.id} withBorder p="sm" radius="md" data-testid={`lane-turn-${turn.id}`}>
              <Group gap="xs" mb={4}>
                <IconUser size={14} />
                <Text size="xs" fw={600} c="dimmed">you</Text>
                <Text size="xs" c="dimmed" ml="auto">
                  {turn.sequence}
                </Text>
              </Group>
              <Text size="sm" style={{ whiteSpace: 'pre-wrap' }} data-testid="lane-turn-input">
                {turn.input}
              </Text>
              <Group gap="xs" mt="sm" mb={4}>
                <IconRobot size={14} />
                <Text size="xs" fw={600} c="dimmed">{LANE_AGENT_LABEL}</Text>
              </Group>
              {turn.output ? (
                <Text size="sm" style={{ whiteSpace: 'pre-wrap' }} data-testid="lane-turn-output">
                  {turn.output}
                </Text>
              ) : (
                <Group gap="xs" data-testid="lane-turn-pending">
                  <Loader size={12} />
                  <Text size="xs" c="dimmed">waiting for the bridge…</Text>
                </Group>
              )}
            </Paper>
          ))}
          <div ref={bottomRef} />
        </Stack>
      </ScrollArea>

      {error && (
        <Text size="sm" c="red" mt="xs" data-testid="lane-chat-error">
          {error}
        </Text>
      )}

      <Group mt="sm" align="flex-end" gap="xs">
        <Textarea
          className="lane-chat-input"
          placeholder="Say something to the lane…"
          autosize
          minRows={1}
          maxRows={6}
          value={text}
          onChange={(e) => setText(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void send();
            }
          }}
          disabled={busy || !ready}
          data-testid="lane-chat-input"
        />
        <Button
          onClick={() => void send()}
          loading={busy}
          disabled={!text.trim() || !ready}
          leftSection={<IconSend size={16} />}
          data-testid="lane-chat-send"
        >
          Send
        </Button>
      </Group>
    </Box>
  );
};

export default LaneChat;
